import { Component, DestroyRef, OnInit, Input, inject } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { skip } from 'rxjs';
import { Analytics10Service } from "src/app/services/analytics10.service";
import { ThemeColorService } from "src/app/services/theme-color.service";
import { ThemePreferenceService } from "src/app/services/theme-preference.service";

@Component({
    selector: "app-p2p-chart",
    templateUrl: "./p2p-chart.component.html",
    styleUrls: ["./p2p-chart.component.css"],
    standalone: false
})
export class P2pChartComponent implements OnInit {
  private service = inject(Analytics10Service);
  private themeColorService = inject(ThemeColorService);
  private themePreferenceService = inject(ThemePreferenceService);
  private destroyRef = inject(DestroyRef);

  basicOptions: any;
  responseData: any = [];
  selectedDays = 90;
  chartType = 'line'
  @Input() pcounts = false;
  @Input() fcounts = false;
  @Input() deId=false;
  chartOptions: any = {
    scales:{y:{}}
  }
  data: any;
  pChartOptions: any = this.createChartOptions();
  selectedP2PRow: any;
  filter:any = {};
  @Input() chartFilter:any = null;

  constructor() {
    this.themePreferenceService.preference$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.pChartOptions = this.createChartOptions();
        this.chartOptions = this.pChartOptions;
        if (this.selectedP2PRow && this.responseData) {
          this.setData(this.selectedP2PRow, this.responseData);
        }
      });
  }

  private createChartOptions(): any {
    return {
      plugins: {
        legend: {
          labels: {
            color: this.themeColorService.resolve('--chart-p2p-legend-text')
          }
        }
      },
      scales: {
        x: {
          ticks: {
            color: this.themeColorService.resolve('--chart-p2p-axis-text')
          }
        },
        y: {
          ticks: {
            color: this.themeColorService.resolve('--chart-p2p-axis-text')
          },
          min: 0
        }
      },
      backgroundColor: 'transparent'
    };
  }
  ngOnInit(): void {
    this.chartType = 'line'
  }

  loadP2PHistory(rowData: any) {
    this.selectedP2PRow = rowData
    this.service.loadP2PHistory(rowData).subscribe((response) => {
      this.responseData = response
      this.setData(rowData, this.responseData);
    });
  }

  updateDays(days: any){
    this.pcounts=false
    this.fcounts=false
    this.selectedDays = days
    if(this.responseData.length==0)
    {
      return;
    }
    this.setData(this.selectedP2PRow, this.responseData);
  }

  getPatientData(){
    this.selectedDays = 90
    this.pcounts = true;
    this.fcounts = false;
    this.chartType = 'bar'
    this.setData(this.selectedP2PRow, this.responseData);
  }

  getFlagsData(){
    this.selectedDays = 90
    this.pcounts = false;
    this.fcounts = true;
    this.chartType = 'bar'
    this.setData(this.selectedP2PRow, this.responseData);
  }

  private clampToNonNegative(value: any): number {
    const numericValue = Number(value);
    if (isNaN(numericValue) || numericValue < 0) {
      return 0;
    }

    return numericValue;
  }

  setData(row: any, response: any) {
    if (!response) {
      return;
    }
    this.selectedP2PRow = row;

    const labels: any[] = []
    const data: any[] = []

    if(this.pcounts != false){
      const records = response['shiftRecords']
      const rawBestFitPoints = (response['bestFitLines'] && response['bestFitLines']['patientTotalTrend'])
        ? response['bestFitLines']['patientTotalTrend']
        : []
      const bestFitPoints = rawBestFitPoints.map((point: any) => this.clampToNonNegative(point))
      const pdata: any[] = []

      this.chartOptions = this.pChartOptions
      records.forEach((rec: any) => {
        let trendLabel = ''
        if(!this.deId)
          trendLabel = this.selectedP2PRow["adminName"] + '- Patient Count'
        else
          trendLabel = "*****, ***** - Patient Count"
        rec.createdAt = this.getLabelString(rec.shiftDisplayDate || rec.shiftDate, true)
        labels.push(rec['createdAt'])
        pdata.push(this.clampToNonNegative(rec['patientCount']))
        this.data = {
          labels: labels,
          datasets: [
            {
              type:'line',
              label:'Regression Trend',
              data: bestFitPoints,
              fill: false,
              borderColor: this.themeColorService.resolve('--chart-p2p-regression'),
              borderDash:[5,5],
              pointRadius:2,
              borderWidth:1,
              backgroundColor: this.themeColorService.resolve('--chart-p2p-regression'),
              tension: 0.4,
              spanGaps: true,
              yAxisID: "y"
            },
            {
              label:
                trendLabel,
              data: pdata, //this.buildChartData(labels, response),
              fill: false,
              borderColor: this.themeColorService.resolve('--chart-p2p-series'),
              backgroundColor: this.themeColorService.resolve('--chart-p2p-series'),
              tension: 0.4,
              spanGaps: true,
              yAxisID: "y"
            }
          ],
        };
    })}

    else if(this.fcounts != false){
      const records = response['shiftRecords']
      const rawBestFitPoints = (response['bestFitLines'] && response['bestFitLines']['shiftTotalsTrend'])
        ? response['bestFitLines']['shiftTotalsTrend']
        : []
      const bestFitPoints = rawBestFitPoints.map((point: any) => this.clampToNonNegative(point))
      const fdata = []
      this.chartOptions = this.pChartOptions
      records.forEach((rec: any) => {
        let trendLabel
        if(!this.deId)
          trendLabel = this.selectedP2PRow["adminName"] + '- Shift Totals'
        else
          trendLabel = "*****, ***** - Shift Totals"
        rec.createdAt = this.getLabelString(rec.shiftDisplayDate || rec.shiftDate, true)
        labels.push(rec['createdAt'])
        fdata.push(this.clampToNonNegative(rec['adminFlagCount']))
        this.data = {
          labels: labels,
          datasets: [
            {
              type:'line',
              label:'Regression Trend',
              data: bestFitPoints,
              fill: false,
              borderColor: this.themeColorService.resolve('--chart-p2p-regression'),
              borderDash:[5,5],
              borderWidth:1,
              pointRadius:2,
              backgroundColor: this.themeColorService.resolve('--chart-p2p-regression'),
              tension: 0.4,
              spanGaps: true,
              yAxisID: "y"
            },
            {
              type:'bar',
              label:
                trendLabel,
              data: fdata, //this.buildChartData(labels, response),
              fill: false,
              borderColor: this.themeColorService.resolve('--chart-p2p-series'),
              backgroundColor: this.themeColorService.resolve('--chart-p2p-series'),
              tension: 0.4,
              spanGaps: true,
              yAxisID: "y"
            }
          ],
        }
    })}

    else{
    const records = response['historyRecords']
    this.chartOptions = this.pChartOptions
    records.forEach((rec: any) => {
      let trendLabel = ''
      if(!this.deId)
        trendLabel = this.selectedP2PRow["adminName"] +" - " +this.selectedP2PRow["adminId"]
      else
        trendLabel = "*****, *****"
      rec.createdAt = this.getLabelString(rec.createdAt)
      labels.push(rec['createdAt'])
      data.push(this.clampToNonNegative(rec['noOfSDEV']))
      this.data = {
        labels: labels,
        datasets: [
          {
            label:
              trendLabel,
            data: data, //this.buildChartData(labels, response),
            fill: false,
            borderColor: this.themeColorService.resolve('--chart-p2p-series'),
            tension: 0.4,
            spanGaps: true,
            yAxisID: "y"
          },
        ],
      };
      
    })
    this.chartType = 'line'
    // this.chartOptions = {}
    }
  }

  getLabelString(date: any, treatAsCalendarDate = false) {
    if (!date) {
      return ''
    }
    const dateStr = String(date)
    const dateOnlyMatch = dateStr.match(
      treatAsCalendarDate ? /^(\d{4})-(\d{2})-(\d{2})/ : /^(\d{4})-(\d{2})-(\d{2})$/
    )
    if (dateOnlyMatch) {
      return `${parseInt(dateOnlyMatch[2], 10)}/${parseInt(dateOnlyMatch[3], 10)}/${parseInt(dateOnlyMatch[1], 10)}`
    }
    date = new Date(date)
    return date.getMonth()+1 + "/" + date.getDate() + "/" + date.getFullYear()
  }

  buildChartData(dates: any, p2pValues:[]){
    const values: any[] = []

    dates.forEach((key: any) =>{
      let p2pValue = null
      p2pValues.forEach( (p2p)=>{
          if (p2p['createdAt'] == key) {
            p2pValue = p2p["noOfSDEV"];
      }}); 
    
      if (p2pValue != 0 && p2pValue != null){
        values.push(this.clampToNonNegative(p2pValue))
      }
    });
    return values.reverse()
  }
}
